<template>
  <div class="session-timeline">
    <div
      v-for="segment in segments"
      :key="segment.key"
      class="session-card"
      :class="`is-${segment.kind}`"
    >
      <div class="sc-header">
        <span
          class="sc-badge"
          :class="`is-${segment.kind}`"
        >
          {{ segment.label }}
        </span>
        <span
          v-if="segment.autoCreated"
          class="sc-badge-auto"
        >
          自動
        </span>
        <span class="sc-time">
          {{ hhmm(segment.startAt) }} → {{ hhmm(segment.releaseAt) }}（{{ segment.durationMinutes }}分）
        </span>
      </div>

      <div class="sc-body">
        <div class="sc-info">
          <div class="sc-name">
            {{ segment.vendibleName }}
          </div>
          <div class="sc-detail">
            単価 ¥{{ segment.unitPrice.toLocaleString() }} × {{ segment.size }}名
          </div>
        </div>
        <div class="sc-price">
          ¥{{ segment.subtotal.toLocaleString() }}
        </div>
      </div>

      <!-- コースの時間変更 -->
      <div v-if="changingKey === segment.key">
        <!-- eslint-disable vue/v-on-event-hyphenation -->
        <!-- NOTE: 同一セグメントは全員同時刻のため、共通1組の入力で全コース履歴に適用する -->
        <persistence-report-card
          v-if="commonPersistenceReport"
          :value="commonPersistenceReport"
          class="my-4"
          @update:startAt="applyCommonStartAt($event)"
          @update:releaseAt="applyCommonReleaseAt($event)"
        />
        <div class="w-full d-flex justify-space-around align-center px-2 pb-2">
          <v-btn
            color="primary"
            :ripple="false"
            :loading="isSubmitting"
            :disabled="!isValidPersistenceReportsToSubmit || isSubmitting"
            @click="updateCourseHistories"
          >
            更新
          </v-btn>
          <v-btn
            :ripple="false"
            color="secondary"
            text
            small
            class="ml-4"
            @click="cancelChanging"
          >
            キャンセル
          </v-btn>
        </div>
      </div>

      <div
        v-else
        class="sc-actions"
      >
        <v-btn
          text
          small
          :ripple="false"
          class="edit-btn"
          aria-label="時間を編集"
          @click="changePersistenceReports(segment)"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiPencilOutline }}
          </v-icon>
          編集
        </v-btn>

        <v-btn
          v-if="cancellable && cancellable.key === segment.key"
          text
          small
          :ripple="false"
          class="cancel-btn"
          aria-label="延長を取り消す"
          @click="confirmCancel(segment)"
        >
          <v-icon
            small
            left
          >
            {{ icons.mdiClose }}
          </v-icon>
          取り消す
        </v-btn>
      </div>
    </div>

    <p
      v-if="segments.length === 0"
      class="ma-0 pa-0 text-sm text--secondary"
    >
      ...
    </p>

    <!-- 取り消し確認ダイアログ -->
    <v-dialog
      v-model="showCancelDialog"
      max-width="360"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          延長の取り消し
        </v-card-title>
        <v-card-text>
          {{ cancelTarget ? `${cancelTarget.label}（${cancelTarget.vendibleName}）を取り消しますか？` : '' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :ripple="false"
            @click="showCancelDialog = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            text
            :ripple="false"
            :loading="isCancelling"
            class="cancel-confirm-btn"
            @click="executeCancel"
          >
            取り消す
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  inject,
  ref,
  computed,
  getCurrentInstance,
  onUnmounted,
} from '@vue/composition-api'
import { each, every } from 'lodash'
import { mdiPencilOutline, mdiClose } from '@mdi/js'
import useCurrentData from '@/views/composable/useCurrentData'
import useDateFormat from '@/views/composable/useDateFormat'
import usePersistenceReport from '@/views/composable/usePersistenceReport'
import {
  groupCourseHistories,
  buildSessionSegments,
  cancellableSegment,
  buildDestroyPayload,
} from '@/views/composable/useCourseSession'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import PersistenceReportCard from '@/views/components/table/PersistenceReportCard.vue'

export default {
  components: {
    PersistenceReportCard,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const vendingHistories = inject('vendingHistories', ref([]))

    const courseHistoriesTemp = ref([])
    const commonPersistenceReport = ref(null)
    const changingKey = ref(null)
    const isSubmitting = ref(false)
    const showCancelDialog = ref(false)
    const cancelTarget = ref(null)
    const isCancelling = ref(false)

    const { currentClub } = useCurrentData()
    const { hhmm } = useDateFormat()
    const {
      isValid: isValidPersistenceReport,
      buildPersistenceReportTemp,
    } = usePersistenceReport()

    const segments = computed(() => {
      return buildSessionSegments(groupCourseHistories(vendingHistories.value, hhmm))
    })

    const cancellable = computed(() => cancellableSegment(segments.value))

    const isValidPersistenceReportsToSubmit = computed(() => {
      return every(courseHistoriesTemp.value, courseHistoryTemp => isValidPersistenceReport(courseHistoryTemp.persistenceReportAttributes))
    })

    const cancelChanging = () => {
      changingKey.value = null
      courseHistoriesTemp.value = []
      commonPersistenceReport.value = null
    }

    // NOTE: 共通入力の時刻をセグメント内の全コース履歴に反映する
    const applyCommonStartAt = value => {
      if (commonPersistenceReport.value) commonPersistenceReport.value.startAt = value
      each(courseHistoriesTemp.value, courseHistoryTemp => {
        // eslint-disable-next-line no-param-reassign
        courseHistoryTemp.persistenceReportAttributes.startAt = value
      })
    }

    const applyCommonReleaseAt = value => {
      if (commonPersistenceReport.value) commonPersistenceReport.value.releaseAt = value
      each(courseHistoriesTemp.value, courseHistoryTemp => {
        // eslint-disable-next-line no-param-reassign
        courseHistoryTemp.persistenceReportAttributes.releaseAt = value
      })
    }

    const updateCourseHistories = async () => {
      isSubmitting.value = true

      try {
        const res = await VendingHistoryApi.updateVendingHistories({
          tableId: props.tableId,
          vendingHistories: courseHistoriesTemp.value,
        })

        if (res?.data) {
          emit('updated:vending-histories', res.data.vendingHistories.data)
          emit('updated:table', res.data.table.data)

          vm.$toast.success(`${currentClub.value.courseAlias}の時間を更新しました`)
        } else {
          vm.$toast.error(`${currentClub.value.courseAlias}の時間の更新に失敗しました`)
        }
      } catch (_e) {
        vm.$toast.error(`${currentClub.value.courseAlias}の時間の更新に失敗しました`)
      } finally {
        isSubmitting.value = false
        cancelChanging()
      }
    }

    // NOTE: 制限時間を表現するための持続レポート（PersistenceReport）を変更する
    const changePersistenceReports = segment => {
      courseHistoriesTemp.value = []
      changingKey.value = segment.key

      each(segment.actualData, courseHistory => {
        const courseHistoryTemp = { id: courseHistory.id }

        const persistenceReport = courseHistory.attributes.persistenceReport.data

        const {
          vendingHistoryId,
          startAt,
          releaseAt,
        } = persistenceReport.attributes

        const persistenceReportTemp = buildPersistenceReportTemp({
          id: persistenceReport.id,
          vendingHistoryId,
          startAt,
          releaseAt,
        })

        courseHistoryTemp.persistenceReportAttributes = persistenceReportTemp
        courseHistoriesTemp.value.push(courseHistoryTemp)
      })

      // NOTE: 共通の時刻入力（セグメントは全員同時刻なので先頭値で初期化）
      const first = courseHistoriesTemp.value[0]?.persistenceReportAttributes
      commonPersistenceReport.value = first
        ? { startAt: first.startAt, releaseAt: first.releaseAt }
        : null
    }

    const confirmCancel = segment => {
      cancelTarget.value = segment
      showCancelDialog.value = true
    }

    const executeCancel = async () => {
      if (!cancelTarget.value) return

      isCancelling.value = true

      try {
        const res = await VendingHistoryApi.updateVendingHistories({
          tableId: props.tableId,
          vendingHistories: buildDestroyPayload(cancelTarget.value),
        })

        if (res?.data) {
          emit('updated:vending-histories', res.data.vendingHistories.data)
          emit('updated:table', res.data.table.data)

          vm.$toast.success('延長を取り消しました')
        } else {
          vm.$toast.error('延長の取り消しに失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長の取り消しに失敗しました')
      } finally {
        isCancelling.value = false
        showCancelDialog.value = false
        cancelTarget.value = null
      }
    }

    onUnmounted(() => cancelChanging())

    return {
      segments,
      cancellable,
      courseHistoriesTemp,
      commonPersistenceReport,
      changingKey,
      isSubmitting,
      showCancelDialog,
      cancelTarget,
      isCancelling,

      isValidPersistenceReportsToSubmit,

      hhmm,
      changePersistenceReports,
      applyCommonStartAt,
      applyCommonReleaseAt,
      cancelChanging,
      updateCourseHistories,
      confirmCancel,
      executeCancel,

      icons: {
        mdiPencilOutline,
        mdiClose,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.session-card {
  background: var(--overview-surface-2, #18181f);
  border: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;

  &.is-extension {
    border-color: var(--overview-accent-soft, rgba(139, 92, 246, 0.25));
  }
}

.sc-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.sc-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;

  &.is-set {
    background: var(--overview-emerald-soft, rgba(52, 211, 153, 0.14));
    color: var(--overview-emerald, #34d399);
  }

  &.is-extension {
    background: var(--overview-accent-softer, rgba(139, 92, 246, 0.14));
    color: var(--overview-accent, #8b5cf6);
  }
}

.sc-badge-auto {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--overview-amber-soft, rgba(251, 191, 36, 0.16));
  color: var(--overview-amber, #f59e0b);
}

.sc-time {
  margin-left: auto;
  font-size: 13px;
  opacity: 0.65;
}

.sc-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sc-name {
  font-size: 15px;
  font-weight: 600;
}

.sc-detail {
  font-size: 13px;
  opacity: 0.6;
}

.sc-price {
  font-size: 17px;
  font-weight: 800;
}

.sc-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

.cancel-btn {
  color: var(--overview-rose, #fb7185) !important;
}
</style>
